/**
 * Buddy Group Service — Plain fetch functions for workflow endpoints.
 *
 * ALL member operations route through /api/buddy-group-ops workflow endpoints
 * which enforce rodeo eligibility, duplicate detection, size limits, and
 * org-membership checks. Generated CRUD endpoints are only used for
 * read-only queries (member list, group metadata).
 *
 * Endpoint map:
 *   POST   /buddy-group-ops/create                           → createBuddyGroup
 *   POST   /buddy-group-ops/join                             → joinBuddyGroup
 *   POST   /buddy-group-ops/validate                         → validateBuddyGroup
 *   PUT    /buddy-group-ops/:id/primary                      → setPrimaryCardholder
 *   GET    /buddy-group-ops/rodeo/:rodeoId/my-group          → getMyGroup
 *   DELETE /buddy-group-ops/:groupId/members/:memberId       → removeBuddyGroupMember
 *
 * IMPORTANT: Do NOT write to CRUD endpoints for member management — those
 * bypass all server-side business rules. Use the workflow endpoints above.
 */

import { httpClient } from '../api/http-client';
import type {
  CreateBuddyGroupRequest,
  CreateBuddyGroupResponse,
  JoinBuddyGroupRequest,
  JoinBuddyGroupResponse,
  ValidateBuddyGroupRequest,
  ValidateBuddyGroupResponse,
  SetPrimaryResponse,
  MyGroupResponse,
  BuddyGroupDetail,
  BuddyGroupMemberView,
  BuddyGroupRodeoEntry,
  BuddyGroupSummary,
  BuddyGroupStatus,
  DecoupledPartneredEntry,
  SetBuddyGroupPreferencesRequest,
  SetBuddyGroupPreferencesResponse,
  PreferencesEditableResponse,
} from '../types/buddy-groups';

// ─── Workflow Endpoints ─────────────────────────────────────────────────────

/** Create a new buddy group for a rodeo. Current user becomes first member. */
export async function createBuddyGroup(
  data: CreateBuddyGroupRequest
): Promise<CreateBuddyGroupResponse> {
  return httpClient.post<CreateBuddyGroupResponse>(
    '/buddy-group-ops/create',
    data
  );
}

/**
 * Join an existing buddy group via the workflow endpoint.
 *
 * This adds the CURRENT AUTHENTICATED USER to the group (not an arbitrary user).
 * The backend enforces: rodeo eligibility, duplicate detection, size limits,
 * org-membership checks, and group status validation.
 *
 * Name + DOB are optional and used for duplicate detection when mode = 'name_dob'.
 */
export async function joinBuddyGroup(
  data: JoinBuddyGroupRequest
): Promise<JoinBuddyGroupResponse> {
  return httpClient.post<JoinBuddyGroupResponse>(
    '/buddy-group-ops/join',
    data
  );
}

/** Pre-flight validation for buddy group membership. */
export async function validateBuddyGroup(
  data: ValidateBuddyGroupRequest
): Promise<ValidateBuddyGroupResponse> {
  return httpClient.post<ValidateBuddyGroupResponse>(
    '/buddy-group-ops/validate',
    data
  );
}

/** Set the primary cardholder for a buddy group. */
export async function setPrimaryCardholder(
  groupId: string,
  userProfileId: string
): Promise<SetPrimaryResponse> {
  return httpClient.put<SetPrimaryResponse>(
    `/buddy-group-ops/${groupId}/primary`,
    { user_profile_id: userProfileId }
  );
}

/**
 * Set or update the per-rodeo performance preferences for a buddy group.
 * Only the primary cardholder may call this. Preferences are scoped to a
 * specific (group, rodeo) attachment — same group attached to two rodeos
 * has independent preferences. Once persisted, every member's existing
 * event registrations in that rodeo are back-filled with these values
 * (this is the conversion path for individual → buddy-group registrations).
 */
export async function setBuddyGroupPreferences(
  groupId: string,
  rodeoId: string,
  payload: Omit<SetBuddyGroupPreferencesRequest, 'rodeo_id'>,
): Promise<SetBuddyGroupPreferencesResponse> {
  return httpClient.put<SetBuddyGroupPreferencesResponse>(
    `/buddy-group-ops/${groupId}/preferences`,
    { ...payload, rodeo_id: rodeoId },
  );
}

/**
 * Check whether the buddy group's preferences for a specific rodeo are
 * still editable (no draw has run yet for any event in that rodeo).
 */
export async function getBuddyGroupPreferencesEditable(
  groupId: string,
  rodeoId: string,
): Promise<PreferencesEditableResponse> {
  const params = new URLSearchParams({ rodeo_id: rodeoId });
  return httpClient.get<PreferencesEditableResponse>(
    `/buddy-group-ops/${groupId}/preferences/editable?${params.toString()}`,
  );
}

/**
 * Attach an existing rodeo-less buddy group to a rodeo so it can carry
 * rodeo-level preferences. Primary cardholder only — backend rejects if
 * the group is already attached to a different rodeo. Returns the list of
 * decoupled partnered entries (Option A) so the success UI can warn that
 * those entries are governed by the pair, not the group.
 */
export async function attachBuddyGroupToRodeo(
  groupId: string,
  rodeoId: string
): Promise<{
  buddy_group_id: string;
  rodeo_id: string;
  decoupled_partnered_entries: DecoupledPartneredEntry[];
}> {
  return httpClient.post<{
    buddy_group_id: string;
    rodeo_id: string;
    decoupled_partnered_entries: DecoupledPartneredEntry[];
  }>(
    `/buddy-group-ops/${groupId}/attach-to-rodeo`,
    { rodeo_id: rodeoId }
  );
}

/**
 * Unregister a buddy group from a specific rodeo. Primary cardholder
 * (or an admin/delegate) only. Removes that one (group, rodeo)
 * attachment and unlinks any event registrations in that rodeo from the
 * group; the group, its members, and any other rodeo attachments survive
 * untouched. The entries themselves are NEVER cancelled — they carry on
 * as ordinary solo entries.
 */
export async function detachBuddyGroupFromRodeo(
  groupId: string,
  rodeoId: string,
): Promise<{ buddy_group_id: string; rodeo_id: string; cleared_registration_count: number }> {
  return httpClient.post<{ buddy_group_id: string; rodeo_id: string; cleared_registration_count: number }>(
    `/buddy-group-ops/${groupId}/detach-from-rodeo`,
    { rodeo_id: rodeoId },
  );
}

export interface WithdrawPreviewEntry {
  registration_id: string;
  event_id: string | null;
  event_name: string | null;
  user_profile_id: string | null;
  member_name: string | null;
}

export interface WithdrawBuddyGroupPreview {
  buddy_group_id: string;
  rodeo_id: string;
  group_name: string | null;
  rodeo_name: string | null;
  is_attached: boolean;
  /** False when the draw has run or the group isn't registered here. */
  can_withdraw: boolean;
  blocked_reason: string | null;
  member_count: number;
  /** Live entries that would be decoupled into solo entries. */
  entry_count: number;
  entries: WithdrawPreviewEntry[];
}

/**
 * Preview the impact of withdrawing a buddy group from one rodeo.
 * Read-only — populates the confirmation dialog so the captain sees the
 * exact entries that will become solo entries (and learns up front when
 * the withdrawal is blocked, instead of after confirming).
 */
export async function previewWithdrawBuddyGroupFromRodeo(
  groupId: string,
  rodeoId: string,
): Promise<WithdrawBuddyGroupPreview> {
  const params = new URLSearchParams({ rodeo_id: rodeoId });
  return httpClient.get<WithdrawBuddyGroupPreview>(
    `/buddy-group-ops/${groupId}/withdraw-preview?${params.toString()}`,
  );
}

export interface BuddyGroupActiveRegistration {
  id: string;
  event_id: string;
  user_profile_id: string;
  position: 'Header' | 'Heeler' | null;
  registration_type: string | null;
  partner_status: string | null;
  buddy_group_position: number | null;
  trig_status_name: string | null;
}

/**
 * List every rodeo this buddy group is registered for (legacy single-rodeo
 * column + every M:N attachment), with rodeo metadata joined to the locked
 * preferences. Returns rodeos in upcoming-first order. Used by the buddy
 * groups list and detail surfaces to surface rodeo info per group.
 */
export async function listBuddyGroupRodeos(
  groupId: string,
): Promise<BuddyGroupRodeoEntry[]> {
  const res = await httpClient.get<{
    rodeos: BuddyGroupRodeoEntry[];
    success: boolean;
  }>(`/buddy-group-ops/${groupId}/rodeos`);
  return res?.rodeos ?? [];
}

/**
 * Active (non-cancelled) event registrations linked to a buddy group.
 * Used by the rodeo detail page to render "registered" indicators on
 * each event card for the current user and their teammates.
 */
export async function getBuddyGroupActiveRegistrations(
  groupId: string
): Promise<BuddyGroupActiveRegistration[]> {
  const res = await httpClient.get<{
    registrations: BuddyGroupActiveRegistration[];
    success: boolean;
  }>(`/buddy-group-ops/${groupId}/active-registrations`);
  return res?.registrations ?? [];
}

/**
 * Preview an invitee's partnered entries that will stay decoupled if they
 * join this buddy group (Option A "decoupled pair" rule). Returns the
 * roping entries the candidate currently has in any rodeo this group is
 * attached to where the partner is NOT in the group. Used by the invite
 * dialog to surface a heads-up before sending. Empty array when there's
 * nothing to warn about.
 */
export interface InviteePartneredEntryPreview {
  rodeo_id: string;
  registration_id: string;
  event_id: string;
  event_name: string | null;
  partner_user_profile_id: string;
  partner_name: string | null;
}

export async function previewInviteePartneredEntries(
  buddyGroupId: string,
  inviteeUserProfileId: string,
): Promise<InviteePartneredEntryPreview[]> {
  const params = new URLSearchParams({
    buddy_group_id: buddyGroupId,
    invitee_user_profile_id: inviteeUserProfileId,
  });
  const res = await httpClient.get<{
    decoupled_partnered_entries: InviteePartneredEntryPreview[];
  }>(`/buddy-group-ops/invite-preview?${params.toString()}`);
  return res?.decoupled_partnered_entries ?? [];
}

/**
 * Get the current user's buddy group for a specific rodeo.
 * Returns the full BuddyGroupDetail with members, or null if not in a group.
 */
export async function getMyGroup(
  rodeoId: string
): Promise<BuddyGroupDetail | null> {
  try {
    const res = await httpClient.get<MyGroupResponse>(
      `/buddy-group-ops/rodeo/${rodeoId}/my-group`
    );
    return res.buddy_group ?? null;
  } catch (err: any) {
    // 400 with no group = user not in a group — not an error
    if (err?.message?.includes('not found') || err?.message?.includes('400')) {
      return null;
    }
    throw err;
  }
}

/**
 * Add a specific user to a buddy group (primary cardholder only).
 * Routes through /buddy-group-ops/:groupId/add-member which enforces
 * all business rules and authorization.
 */
export async function addMemberToGroup(
  groupId: string,
  userProfileId: string,
  firstName?: string,
  lastName?: string,
  dateOfBirth?: string
): Promise<{ member: { id: string; buddy_group_id: string; user_profile_id: string; is_primary: boolean }; group_size: number }> {
  return httpClient.post(`/buddy-group-ops/${groupId}/add-member`, {
    user_profile_id: userProfileId,
    ...(firstName ? { first_name: firstName } : {}),
    ...(lastName ? { last_name: lastName } : {}),
    ...(dateOfBirth ? { date_of_birth: dateOfBirth } : {}),
  });
}

/** Search result from the member discovery endpoint. */
export interface SearchUserResult {
  user_profile_id: string;
  display_name: string;
  member_number: string | null;
  city: string | null;
  state: string | null;
}

/**
 * Search users by name or member number for buddy group member discovery.
 * Routes through /buddy-group-ops/search-users which performs server-side
 * ilike search on user_profiles and organization_memberships.
 */
export async function searchUsersForGroup(
  query: string,
  rodeoId?: string,
  excludeGroupId?: string,
  limit: number = 10
): Promise<SearchUserResult[]> {
  const params = new URLSearchParams({
    q: query,
    limit: String(limit),
  });
  if (rodeoId) {
    params.set('rodeo_id', rodeoId);
  }
  if (excludeGroupId) {
    params.set('exclude_group_id', excludeGroupId);
  }
  const res = await httpClient.get<{ results: SearchUserResult[] }>(
    `/buddy-group-ops/search-users?${params.toString()}`
  );
  return res?.results ?? [];
}

// ─── Read-Only CRUD Helpers ─────────────────────────────────────────────────
// These use generated CRUD endpoints for READ-ONLY queries only.
// Write operations MUST go through workflow endpoints above.

/** Safely cast a string to BuddyGroupStatus, returning null if invalid. */
function parseBuddyGroupStatus(value: unknown): BuddyGroupStatus | null {
  const valid = ['pending', 'active', 'locked', 'disbanded'];
  return typeof value === 'string' && valid.includes(value)
    ? (value as BuddyGroupStatus)
    : null;
}

/**
 * List all buddy groups the current user belongs to.
 *
 * Strategy:
 *   1. Query /buddy-group-members filtered to user to find memberships.
 *   2. For each unique rodeo, call the workflow endpoint getMyGroup(rodeoId)
 *      which returns enriched BuddyGroupDetail with accurate status.
 *   3. Fall back to CRUD assembly only if the workflow endpoint fails.
 *
 * The workflow endpoint is preferred because generated CRUD routes do NOT
 * return `status` or `primary_member_user_profile_id` (entity config was
 * updated but routes were not regenerated).
 */
export async function listMyBuddyGroups(
  userProfileId: string
): Promise<BuddyGroupSummary[]> {
  // Step 1: Get all buddy_group_members for this user
  const memberships = await httpClient.get<{
    data: Array<{
      id: string;
      buddy_group_id: string;
      user_profile_id: string;
      is_primary: boolean;
    }>;
    total: number;
  }>(`/buddy-group-members?user_profile_id=${userProfileId}&limit=100`);

  if (!memberships?.data?.length) return [];

  // Step 2: For each membership, get group data with real status
  const groups: BuddyGroupSummary[] = [];

  for (const membership of memberships.data) {
    if (!membership.buddy_group_id) continue;
    try {
      // Get group base info from CRUD (wrapped in { data, success })
      const crudRes = await httpClient.get<{
        data: {
          id: string;
          group_name: string | null;
          rodeo_id: string | null;
          status?: string | null;
          primary_member_user_profile_id?: string | null;
        };
      }>(`/buddy-groups/${membership.buddy_group_id}`);
      const group = crudRes?.data;

      // Try the workflow endpoint for accurate status and member data.
      // Only attempt when rodeo_id is present — groups created without a
      // rodeo have no rodeo-scoped workflow endpoint to call.
      let realStatus: BuddyGroupStatus | null = null;
      let memberCount = 0;
      let primaryUserProfileId: string | null = null;

      if (group.rodeo_id) {
        try {
          const enriched = await getMyGroup(group.rodeo_id);
          if (enriched && enriched.id === membership.buddy_group_id) {
            realStatus = enriched.status;
            memberCount = enriched.members?.length ?? 0;
            primaryUserProfileId = enriched.primary_member_user_profile_id;
          }
        } catch {
          // Workflow endpoint failed — fall through to CRUD data
        }
      }

      // If workflow didn't return this group, fall back to CRUD data
      if (realStatus === null) {
        const allMembers = await httpClient.get<{
          data: Array<{
            id: string;
            user_profile_id: string;
            is_primary: boolean;
          }>;
          total: number;
        }>(
          `/buddy-group-members?buddy_group_id=${membership.buddy_group_id}&limit=20`
        );

        const memberList = allMembers?.data ?? [];
        const primaryMember = memberList.find((m) => m.is_primary);
        primaryUserProfileId = primaryMember?.user_profile_id ?? null;
        memberCount = memberList.length;
        // Use CRUD-returned status if column present; otherwise 'pending'
        // (conservative — not 'active') so locked/disbanded groups are not
        // misrepresented as editable.
        realStatus = parseBuddyGroupStatus(group.status) ?? 'pending';
      }

      // Pending invitation count — best-effort; on error treat as 0
      // so the rest of the list still renders.
      let pendingInvitationCount = 0;
      try {
        const inv = await httpClient.get<{
          data: Array<{ id: string; status: string }>;
        }>(
          `/buddy-group-invitations?buddy_group_id=${group.id}&status=pending&limit=20`
        );
        pendingInvitationCount = (inv?.data ?? []).filter(
          (i) => i.status === 'pending'
        ).length;
      } catch {
        // Ignore — pending count is informational only.
      }

      // Pull every M:N attachment row for this group so the caller can
      // reason about all rodeos the group is registered for at once.
      // Pre-attachments environments fall back to the legacy
      // buddy_groups.rodeo_id (a single value, surfaced as a 1-element
      // array) so the caller's filter logic stays uniform.
      let attachedRodeoIds: string[] = [];
      try {
        const att = await httpClient.get<{
          data: Array<{ rodeo_id: string }>;
        }>(
          `/buddy-group-rodeo-attachments?buddy_group_id=${group.id}&limit=50`,
        );
        attachedRodeoIds = (att?.data ?? [])
          .map((r) => r.rodeo_id)
          .filter((id): id is string => Boolean(id));
      } catch {
        // Endpoint not available yet (pre-migration) — fall through.
      }
      if (attachedRodeoIds.length === 0 && group.rodeo_id) {
        attachedRodeoIds = [group.rodeo_id];
      }

      groups.push({
        id: group.id,
        group_name: group.group_name,
        rodeo_id: group.rodeo_id ?? null,
        attached_rodeo_ids: attachedRodeoIds,
        primary_member_user_profile_id: primaryUserProfileId,
        status: realStatus,
        member_count: memberCount,
        pending_invitation_count: pendingInvitationCount,
        is_primary: membership.is_primary ?? false,
      });
    } catch {
      // Skip groups that can't be fetched (deleted, etc.)
    }
  }

  return groups;
}

/**
 * Update a buddy group name via generated CRUD endpoint.
 */
export async function updateBuddyGroupName(
  groupId: string,
  groupName: string
): Promise<void> {
  await httpClient.put(`/buddy-groups/${groupId}`, {
    group_name: groupName,
  });
}

export interface LeavePreviewRegistration {
  registration_id: string;
  event_id: string | null;
  event_name: string | null;
}

export interface LeavePreviewPrimaryEvent extends LeavePreviewRegistration {
  outcome: 'converted' | 'kept_drawn';
  reason: string | null;
}

export interface LeaveBuddyGroupPreview {
  group_name: string | null;
  group_status: string;
  is_primary: boolean;
  leaver_registrations: LeavePreviewRegistration[];
  will_disband: boolean;
  primary_cascade_preview: {
    primary_profile_id: string;
    events: LeavePreviewPrimaryEvent[];
  } | null;
}

export interface LeaveBuddyGroupResult {
  success: boolean;
  group_disbanded: boolean;
  leaver_unlinked_registrations: LeavePreviewRegistration[];
  primary_cascade: {
    primary_profile_id: string;
    events: LeavePreviewPrimaryEvent[];
  } | null;
}

/**
 * Preview the cascade that will run if the current user leaves this group.
 * Read-only — used to populate the "Leave Buddy Group" confirmation modal.
 */
export async function previewLeaveBuddyGroup(
  groupId: string,
): Promise<LeaveBuddyGroupPreview> {
  return httpClient.get<LeaveBuddyGroupPreview>(
    `/buddy-group-ops/${groupId}/leave-preview`,
  );
}

/**
 * Leave a buddy group via workflow endpoint.
 * The leaver's group-tagged entries are decoupled into individual entries
 * — never deleted — so the rider stays entered in those events. If the
 * group falls below minimum size, the surviving primary's registrations
 * are converted too and they are notified.
 */
export async function leaveBuddyGroup(
  groupId: string,
): Promise<LeaveBuddyGroupResult> {
  return httpClient.post<LeaveBuddyGroupResult>(
    `/buddy-group-ops/${groupId}/leave`,
  );
}

/**
 * Delete a buddy group via workflow endpoint.
 * Notifies all members/invitees, then cascade-deletes.
 */
export async function deleteBuddyGroup(groupId: string): Promise<void> {
  await httpClient.delete(`/buddy-group-ops/${groupId}`);
}

/**
 * Remove a member from a buddy group via the workflow endpoint.
 *
 * Routes through /buddy-group-ops/:groupId/members/:memberId which enforces:
 * - Only the primary cardholder can remove members
 * - Cannot remove yourself (transfer primary first)
 * - Cannot remove the primary cardholder
 * - Cannot modify locked/disbanded groups
 */
export async function removeBuddyGroupMember(
  groupId: string,
  memberId: string
): Promise<{ success: boolean; group_size: number }> {
  return httpClient.delete<{ success: boolean; group_size: number }>(
    `/buddy-group-ops/${groupId}/members/${memberId}`
  );
}

/**
 * Fetch a buddy group's accepted members with display names resolved
 * from user_profiles. Used by surfaces that pick a group from a list
 * (e.g. the rodeo-level setup page) where seeing the actual people
 * matters more than just a count. Errors per-profile fall back to
 * `display_name: null` so a single missing profile doesn't break the
 * list. Pending invitations are NOT included — those callers want
 * "who's actually on the team" for picking purposes.
 */
export async function getBuddyGroupMembersWithNames(
  groupId: string,
): Promise<BuddyGroupMemberView[]> {
  const memberRecords = await getBuddyGroupMembers(groupId);
  if (memberRecords.length === 0) return [];
  return Promise.all(
    memberRecords.map(async (m) => {
      let displayName: string | null = null;
      try {
        const profileRes = await httpClient.get<{
          data: {
            display_first_name?: string | null;
            display_last_name?: string | null;
            first_name?: string | null;
            last_name?: string | null;
          };
        }>(`/user-profiles/${m.user_profile_id}`);
        const p = profileRes?.data;
        const first = p?.display_first_name ?? p?.first_name ?? '';
        const last = p?.display_last_name ?? p?.last_name ?? '';
        displayName = [first, last].filter(Boolean).join(' ') || null;
      } catch {
        // Profile lookup failed — leave display_name as null.
      }
      return {
        id: m.id,
        user_profile_id: m.user_profile_id,
        is_primary: m.is_primary ?? false,
        display_name: displayName,
        member_number: null,
        invitation_status: 'accepted' as const,
      };
    }),
  );
}

/**
 * Get the members of a buddy group via generated CRUD.
 */
export async function getBuddyGroupMembers(
  groupId: string
): Promise<
  Array<{
    id: string;
    buddy_group_id: string;
    user_profile_id: string;
    is_primary: boolean;
  }>
> {
  const res = await httpClient.get<{
    data: Array<{
      id: string;
      buddy_group_id: string;
      user_profile_id: string;
      is_primary: boolean;
    }>;
    total: number;
  }>(`/buddy-group-members?buddy_group_id=${groupId}&limit=20`);
  return res?.data ?? [];
}

// ─── Invitation Functions ──────────────────────────────────────────────────
// NOTE: No backend invitation workflow exists yet. The backend has no
// invitation status on buddy_group_members and no accept/decline endpoints.
//
// The previous getPendingInvitations() function was removed because it
// incorrectly treated non-primary memberships as pending invitations.
// Being a non-primary member means the user joined the group (voluntarily
// via /join) — NOT that they have a pending invitation to respond to.
//
// To implement real invitations, the backend needs:
//   1. An invitation_status field on buddy_group_members (or a separate table)
//   2. POST /buddy-group-ops/invite — create invitation
//   3. POST /buddy-group-ops/invite/:id/accept
//   4. POST /buddy-group-ops/invite/:id/decline
//   5. GET /buddy-group-ops/invitations — list pending invitations
